import { ICellRendererParams } from 'ag-grid-community'
import { useEffect, useMemo, useState } from 'react'
import { FunctionProperty } from '../../../../../lib/commons/appFunction'
import Tooltip from '../../../../components/tooltips/Tooltip'
import { FloatingEditableIcon } from '../../../commons/AgGrid/components/cell/common/text'
import { setError } from '../../lib/validation'
import validator from '../../../meta/validator'
import { StaleIndicatorIcon } from '../../../../components/icons/StaleIndicatorIcon'

interface Props extends ICellRendererParams {
  tooltip?: (params: ICellRendererParams) => string | undefined
  uiMeta?: FunctionProperty
  validate?: (value: any, params: ICellRendererParams) => string | undefined
  stale?: (params: ICellRendererParams) => boolean
  staleMessage?: string
  // When true, render StaleIndicatorIcon before the text in DOM.
  // For RTL cells (e.g. path columns), this makes the icon appear at the right end visually.
  staleIconAtStart?: boolean
}

export const DefaultCellRenderer = (props: Props) => {
  const {
    value,
    valueFormatted,
    data,
    column,
    node,
    tooltip,
    uiMeta,
    validate,
    stale,
    staleMessage,
    staleIconAtStart,
  } = props
  const [message, setMessage] = useState<string | undefined>()
  const [tooltipText, setTooltipText] = useState<string | undefined>()

  useEffect(() => {
    const err = validate
      ? validate(value, props)
      : uiMeta
        ? validator.validate(value, data, uiMeta, () => undefined)?.getMessage()
        : undefined
    setError(data, err, props)
    setMessage(err)
  }, [value])

  useEffect(() => {
    tooltip && setTooltipText(tooltip?.(props))
  }, [value])

  const isStale = useMemo(() => !!stale?.(props), [stale, props])
  const staleIcon = isStale && staleMessage && (
    <StaleIndicatorIcon message={staleMessage} />
  )

  return (
    <Tooltip message={message ?? tooltipText}>
      <div
        id={`column-${column?.getColId()}-${node?.rowIndex}`}
        className={'sevend-ag-cell'}
        style={{ backgroundColor: !!message ? '#FADBD8' : undefined }}
      >
        {staleIconAtStart && staleIcon}
        {valueFormatted ??
          (value !== null && typeof value === 'object' ? '' : value)}
        {!staleIconAtStart && staleIcon}
        <FloatingEditableIcon {...props} />
      </div>
    </Tooltip>
  )
}
